import React from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Icon,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Logo from "../../../../assets/logo/text-logo.png";
import {
  businessAdminRoutes,
  ExtendedRouteObject,
} from "../../business-admin.routes";
import { useAuth } from "../../../auth/hooks/useAuth";
import { hasPermission } from "../../../auth/utils/permission.utils";

const TRANSITION_DURATION = 0.2;
const HOVER_ANIMATION = {
  transition: `all ${TRANSITION_DURATION}s cubic-bezier(0.4, 0, 0.2, 1)`,
};

// Helper function to get menu title
const getMenuTitle = (path: string): string => {
  const lastSegment = path.split("/").pop() || "";
  return lastSegment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Helper function to check if user has permission for a route
const hasRoutePermission = (
  route: ExtendedRouteObject,
  permissions: any,
  isStoreOwner: boolean = false
): boolean => {
  // Store owners have access to everything
  if (isStoreOwner) {
    return true;
  }

  // If route has no permission requirement, it's accessible to everyone
  if (!route.permission) {
    return true;
  }

  // Check if user has the required permission
  // return hasPermission(route.permission, permissions, isStoreOwner);
  return true;
};

interface MenuItem {
  id: string;
  text: string;
  icon?: string;
  path: string;
  subItems?: MenuItem[];
}

interface RouteConfig {
  path?: string;
  children?: RouteConfig[];
  element?: React.ReactNode;
  index?: boolean;
  handle?: { hideSidebar?: boolean };
  id?: string;
}

// Function to generate menu items from routes
const generateMenuItems = (permissions: any, isStoreOwner: boolean = false) => {
  const menuItems: MenuItem[] = [];
  const mainRoutes = businessAdminRoutes[0].children as ExtendedRouteObject[];

  if (!mainRoutes) return menuItems;

  // Add Dashboard as the first item if user has permission or is store owner
  const dashboardRoute = mainRoutes.find((route) => !route.path && route.index);
  if (
    dashboardRoute &&
    hasRoutePermission(dashboardRoute, permissions, isStoreOwner)
  ) {
    menuItems.push({
      id: "dashboard",
      text: "Dashboard",
      icon: dashboardRoute?.icon || "dashboard",
      path: "/business",
    });
  }

  mainRoutes.forEach((route: ExtendedRouteObject) => {
    if (route.path && !route.index && !route.handle?.hideSidebar) {
      const fullPath = `/business/${route.path}`;
      const moduleId = route.id || route.path;

      // Check if any child routes are viewable (bypass check if store owner)
      const hasViewableChildren =
        isStoreOwner ||
        route.children?.some((subRoute) => {
          if (!subRoute.path || !subRoute.id) return false;
          return hasRoutePermission(subRoute, permissions, isStoreOwner);
        });

      // Check if user has permission for this route or any of its children
      const hasMainRoutePermission = hasRoutePermission(
        route,
        permissions,
        isStoreOwner
      );

      // Skip if user doesn't have permission to view this module AND doesn't have permission to view any children
      // Unless they are a store owner
      if (
        !moduleId ||
        (!isStoreOwner && !hasMainRoutePermission && !hasViewableChildren)
      ) {
        return;
      }

      const menuItem: MenuItem = {
        id: moduleId,
        text: getMenuTitle(route.path),
        icon: route.icon,
        path: fullPath,
      };

      // Only add subItems if the route has children that aren't hidden
      if (route.children) {
        menuItem.subItems = route.children
          .filter((subRoute) => {
            if (
              !subRoute.path ||
              !subRoute.id ||
              subRoute.index ||
              subRoute.handle?.hideSidebar
            ) {
              return false;
            }
            return hasRoutePermission(subRoute, permissions, isStoreOwner);
          })
          .map(
            (subRoute: ExtendedRouteObject): MenuItem => ({
              id: subRoute.id as string,
              text: getMenuTitle(subRoute.path || ""),
              icon: subRoute.icon,
              path: `${fullPath}/${subRoute.path}`,
            })
          );

        // Only add the menu item if it has visible subitems or user has permission to the main route
        if (menuItem.subItems.length > 0 || hasMainRoutePermission) {
          menuItems.push(menuItem);
        }
      } else {
        menuItems.push(menuItem);
      }
    }
  });

  return menuItems;
};

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  onLogout: () => void;
  currentPath: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  drawerWidth,
  mobileOpen,
  onDrawerToggle,
  onLogout,
  currentPath,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
  const [openSubMenus, setOpenSubMenus] = React.useState<{
    [key: string]: boolean;
  }>({});

  const menuItems = React.useMemo(
    () => generateMenuItems(user?.permissions, user?.is_store_owner || false),
    [user?.permissions, user?.is_store_owner]
  );

  React.useEffect(() => {
    // Initialize open state based on current path
    const initialOpenState = menuItems.reduce((acc, item) => {
      if (item.subItems) {
        acc[item.text] =
          currentPath === item.path ||
          item.subItems.some((sub) => currentPath === sub.path);
      }
      return acc;
    }, {} as { [key: string]: boolean });
    setOpenSubMenus(initialOpenState);
  }, [currentPath, menuItems]);

  const toggleSubMenu = (itemText: string) => {
    setOpenSubMenus((prev) => ({
      ...prev,
      [itemText]: !prev[itemText],
    }));
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onDrawerToggle();
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        borderRight: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.06)",
      }}
    >
      {/* Sidebar Header */}
      <Box
        sx={{
          p: 3,
          height: 72,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.06)",
        }}
      >
        <Box
          sx={{
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            overflow: "hidden",
            ...HOVER_ANIMATION,
          }}
        >
          <img
            src={Logo}
            alt="Logo"
            style={{
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      {/* Navigation Menu */}
      <List
        sx={{
          px: 2,
          py: 3,
          flex: 1,
        }}
      >
        {menuItems.map((item) => {
          const isSelected =
            currentPath === item.path ||
            (item.subItems &&
              item.subItems.some((sub) => currentPath === sub.path));
          const isHovered = hoveredItem === item.path;
          const isSubMenuOpen = openSubMenus[item.text] || false;

          return (
            <React.Fragment key={item.text}>
              <ListItem
                onClick={() => {
                  if (item.subItems) {
                    toggleSubMenu(item.text);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                onMouseEnter={() => setHoveredItem(item.path)}
                onMouseLeave={() => setHoveredItem(null)}
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  mb: 0.5,
                  py: 1.5,
                  px: 2,
                  borderRadius: 1,
                  color: isSelected ? "#111827" : "#6B7280",
                  bgcolor: isSelected ? "rgba(0, 0, 0, 0.03)" : "transparent",
                  ...HOVER_ANIMATION,
                  "&:hover": {
                    color: "#111827",
                    bgcolor: "rgba(0, 0, 0, 0.02)",
                    "& .menu-icon": {
                      color: "#111827",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 36,
                    color: "inherit",
                  }}
                >
                  <Icon
                    className="menu-icon"
                    sx={{
                      fontSize: 20,
                      ...HOVER_ANIMATION,
                    }}
                  >
                    {item.icon}
                  </Icon>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 600 : 500,
                        fontSize: "0.875rem",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {item.text}
                    </Typography>
                  }
                />
                {item.subItems && (
                  <Icon
                    sx={{
                      fontSize: 18,
                      transition: "transform 0.3s",
                      transform: isSubMenuOpen
                        ? "rotate(-180deg)"
                        : "rotate(0)",
                      opacity: 0.6,
                    }}
                  >
                    expand_more
                  </Icon>
                )}
              </ListItem>

              {/* Sub Menu Items */}
              {item.subItems && isSubMenuOpen && (
                <List
                  sx={{
                    pl: 4,
                    pr: 1,
                    mb: 1,
                  }}
                >
                  {item.subItems.map((subItem) => {
                    const isSubSelected = currentPath === subItem.path;
                    const isSubHovered = hoveredItem === subItem.path;

                    return (
                      <ListItem
                        key={subItem.text}
                        onClick={() => handleNavigation(subItem.path)}
                        onMouseEnter={() => setHoveredItem(subItem.path)}
                        onMouseLeave={() => setHoveredItem(null)}
                        sx={{
                          position: "relative",
                          cursor: "pointer",
                          mb: 0.5,
                          py: 1,
                          px: 2,
                          borderRadius: 1,
                          color: isSubSelected ? "#111827" : "#6B7280",
                          ...HOVER_ANIMATION,
                          "&:hover": {
                            color: "#111827",
                            bgcolor: "rgba(0, 0, 0, 0.02)",
                            "& .submenu-icon": {
                              color: "#111827",
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: "inherit",
                          }}
                        >
                          <Icon
                            className="submenu-icon"
                            sx={{
                              fontSize: 18,
                              ...HOVER_ANIMATION,
                            }}
                          >
                            {subItem.icon}
                          </Icon>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isSubSelected ? 600 : 500,
                                fontSize: "0.8125rem",
                                letterSpacing: "0.01em",
                              }}
                            >
                              {subItem.text}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Sidebar Footer */}
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.06)",
        }}
      >
        <Button
          fullWidth
          onClick={onLogout}
          variant="text"
          size="large"
          startIcon={<Icon>logout</Icon>}
          sx={{
            py: 1.5,
            color: "#6B7280",
            fontSize: "0.875rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
            justifyContent: "flex-start",
            ...HOVER_ANIMATION,
            "&:hover": {
              color: "#111827",
              bgcolor: "rgba(0, 0, 0, 0.02)",
            },
          }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidth },
        flexShrink: { sm: 0 },
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: "#ffffff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            background: "#ffffff",
            boxShadow: "none",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};
